import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { NAV_LINKS, CAREERS } from "@/components/vss/data";

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const jump = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.9, ease: [0.21, 0.47, 0.32, 0.98], delay: 0.15 }}
      className="fixed inset-x-0 top-0 z-50 flex justify-center px-4 pt-4 sm:pt-6"
      data-testid="site-nav"
    >
      <nav
        className={`w-full max-w-6xl transition-all duration-500 ${
          scrolled
            ? "backdrop-blur-xl bg-white/85 border border-slate-200 shadow-[0_10px_32px_rgba(15,23,42,0.06)]"
            : "bg-white/40 border border-transparent"
        } rounded-full px-3 sm:px-4 py-2.5 flex items-center justify-between`}
      >
        <button
          onClick={() => jump("top")}
          className="flex items-center gap-2 sm:gap-3 group pl-1"
          data-testid="nav-brand"
        >
          <img
            src="/logo.png"
            alt="Visionary Staffing Solutions LLP"
            className="h-10 sm:h-12 w-auto"
          />
        </button>

        <div className="hidden lg:flex items-center gap-8">
          {NAV_LINKS.map((l) =>
            l.href ? (
              <a
                key={l.id}
                href={l.href}
                data-testid={`nav-link-${l.id}`}
                className="text-[13px] font-semibold tracking-wide text-slate-700 hover:text-teal-600 transition link-underline"
              >
                {l.label}
              </a>
            ) : (
              <button
                key={l.id}
                onClick={() => jump(l.id)}
                data-testid={`nav-link-${l.id}`}
                className="text-[13px] font-semibold tracking-wide text-slate-700 hover:text-teal-600 transition link-underline"
              >
                {l.label}
              </button>
            )
          )}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => jump("contact")}
            data-testid="nav-request-talent-btn"
            className="group hidden sm:inline-flex items-center gap-2 rounded-full px-4 py-2 text-[12px] font-semibold tracking-wide bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-white hover:brightness-110 transition shadow-[0_6px_18px_rgba(20,184,166,0.28)]"
          >
            Request Talent
            <ArrowUpRight className="h-3.5 w-3.5 transition group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
          </button>
          <a
            href={CAREERS.register}
            data-testid="nav-join-talent-network-btn"
            className="hidden md:inline-flex items-center rounded-full px-4 py-2 text-[12px] font-semibold tracking-wide border border-teal-600/40 text-teal-700 hover:bg-teal-50 transition"
          >
            Join Our Talent Network
          </a>
          <button
            onClick={() => setOpen((v) => !v)}
            data-testid="nav-menu-toggle"
            className="lg:hidden inline-flex items-center justify-center h-9 w-9 rounded-full border border-slate-300 text-slate-700"
            aria-label="Toggle menu"
          >
            {open ? <X className="h-4 w-4" /> : <Menu className="h-4 w-4" />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            key="mobile-menu"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            className="lg:hidden absolute top-[72px] inset-x-4 rounded-2xl border border-slate-200 bg-white/95 backdrop-blur-xl p-4 shadow-[0_20px_40px_rgba(15,23,42,0.08)]"
            data-testid="mobile-menu"
          >
            <div className="flex flex-col gap-1">
              {NAV_LINKS.map((l) =>
                l.href ? (
                  <a
                    key={l.id}
                    href={l.href}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="text-left px-3 py-3 rounded-xl text-slate-700 hover:bg-slate-100"
                  >
                    {l.label}
                  </a>
                ) : (
                  <button
                    key={l.id}
                    onClick={() => jump(l.id)}
                    data-testid={`mobile-nav-link-${l.id}`}
                    className="text-left px-3 py-3 rounded-xl text-slate-700 hover:bg-slate-100"
                  >
                    {l.label}
                  </button>
                )
              )}
              <button
                onClick={() => jump("contact")}
                data-testid="mobile-request-talent-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold bg-gradient-to-r from-cyan-500 via-teal-500 to-emerald-500 text-white"
              >
                Request Talent <ArrowUpRight className="h-4 w-4" />
              </button>
              <a
                href={CAREERS.register}
                data-testid="mobile-join-talent-network-btn"
                className="mt-2 inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3 text-sm font-semibold border border-teal-600/40 text-teal-700"
              >
                Join Our Talent Network
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
